function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useDidUpdate, useStateInRef } from 'seagull';
import { useEventReporter, useStore, useStoreActions, useIoContext, useStoreStatic } from "../contexts";
import { selectors } from "../store/state-selectors";
import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
function makeUserProperties(userInfo) {
  const userId = userInfo === null || userInfo === void 0 ? void 0 : userInfo.id();
  const {
    type,
    hasLinkedBankAccount,
    invitedUserCount,
    membersCount,
    ownership,
    kycCompletionStatus,
    businessTypes,
    csm,
    postalAddress,
    numSaleAgreements,
    numPurchaseAgreements,
    numSaleInvoices,
    numPurchaseInvoices,
    numSampleAgreements,
    role
  } = userInfo.business() || {};
  const firstName = userInfo.firstName();
  const lastName = userInfo.lastName();
  const phone = userInfo.phone();
  const referredBy = userInfo.referredByBusinessId();
  const numClientsAMonth = userInfo.numClientsAMonth();
  return _objectSpread({
    id: userId,
    type: type,
    firstName,
    lastName,
    phone,
    numClientsAMonth,
    hasLinkedBankAccount,
    invitedUserCount,
    membersCount,
    ownership,
    kycCompletionStatus,
    numSaleAgreementsApproved: numSaleAgreements === null || numSaleAgreements === void 0 ? void 0 : numSaleAgreements.approved,
    numSaleAgreementsPending: numSaleAgreements === null || numSaleAgreements === void 0 ? void 0 : numSaleAgreements.pending,
    numPurchaseAgreementsApproved: numPurchaseAgreements === null || numPurchaseAgreements === void 0 ? void 0 : numPurchaseAgreements.approved,
    numPurchaseAgreementsPending: numPurchaseAgreements === null || numPurchaseAgreements === void 0 ? void 0 : numPurchaseAgreements.pending,
    numSaleInvoicesPaid: numSaleInvoices === null || numSaleInvoices === void 0 ? void 0 : numSaleInvoices.paid,
    numSaleInvoicesPending: numSaleInvoices === null || numSaleInvoices === void 0 ? void 0 : numSaleInvoices.pending,
    numPurchaseInvoicesPaid: numPurchaseInvoices === null || numPurchaseInvoices === void 0 ? void 0 : numPurchaseInvoices.paid,
    numPurchaseInvoicesPending: numPurchaseInvoices === null || numPurchaseInvoices === void 0 ? void 0 : numPurchaseInvoices.pending,
    numSampleAgreementsApproved: numSampleAgreements === null || numSampleAgreements === void 0 ? void 0 : numSampleAgreements.approved,
    numSampleAgreementsPending: numSampleAgreements === null || numSampleAgreements === void 0 ? void 0 : numSampleAgreements.pending,
    businessTypes,
    referredBy,
    csm,
    role
  }, postalAddress);
}
export default function AuthProvider(props) {
  const getProps = useStateInRef(props);
  const authToken = useStore(state => state.authToken);
  const userInfo = useStore(state => state.userInfo);
  const shouldLoadAppcues = useStore(selectors.shouldLoadAppcues);
  const [appcuesLoaded, setAppcuesLoaded] = useState(false);
  const {
    getState
  } = useStoreStatic();
  const isRealUser = useStore(selectors.isRealUser) && !!userInfo;
  const {
    reset: resetStore
  } = useStoreActions();
  const {
    getHotjar,
    cookies
  } = useIoContext();
  const bi = useEventReporter();
  const getBI = useStateInRef(bi);
  useDidUpdate(() => {
    if (authToken == null) {
      cookies.setCookie('AuthToken', '', {
        expires: new Date(0)
      });
      cookies.trySignalAuthTokenChange();
      resetStore(true);
    }
  }, [authToken]);
  useEffect(() => {
    if (shouldLoadAppcues) {
      getProps().loadAppcues(getState().session.appcuesAccountId).then(() => setAppcuesLoaded(true));
    }
  }, [shouldLoadAppcues]);
  useEffect(() => {
    const {
      heapIdentify,
      heapResetIdentity
    } = getBI();
    if (isRealUser) {
      var _getProps$visitorSync;
      const userId = userInfo.id() || '';
      const email = userInfo.email() || '';
      const userProperties = makeUserProperties(userInfo);
      Sentry.setUser({
        id: userId
      }); //For future reference - the reason we're not sending user email is because of GDPR reasons
      heapIdentify(userId, email, userProperties);
      getHotjar().identify(userId, _objectSpread({
        sessionId: window._sessionId
      }, userProperties));
      getProps().visitorSyncOrigin !== '' && ((_getProps$visitorSync = getProps().visitorSyncFrame) === null || _getProps$visitorSync === void 0 ? void 0 : _getProps$visitorSync.postMessage({
        type: 'anc_tracker_request',
        action: 'sync_user',
        data: {
          userId,
          businessId: userInfo.businessId(),
          properties: {},
          origin: window.location.origin,
          path: window.location.pathname
        }
      }, "https://".concat(getProps().visitorSyncOrigin)));
    } else {
      heapResetIdentity();
    }
  }, [userInfo, isRealUser]);
  useEffect(() => {
    if (appcuesLoaded && isRealUser) {
      const userId = userInfo.id() || '';
      const email = userInfo.email() || '';
      const userProperties = makeUserProperties(userInfo);
      getBI().appcuesIdentify(userId, email, userProperties);
    }
  }, [isRealUser, userInfo, appcuesLoaded]);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: props.children
  });
}